/**
 * voice-v8 — Changelog-Ansicht (pre.856, Martins Wunsch).
 *
 * Sidebar-Button „Alle Änderungen" → holt /api/changelog (Markdown) und zeigt
 * es sauber gerendert in einem Modal-Sheet. Nutzt die etablierten
 * Sheet-Bausteine (buildSheetHeader/.sheet--modal) + den geteilten
 * Markdown-Renderer (DRY — kein eigenes Format-System).
 *
 * 05.10.2026 (Ticket 2931): Knopf und Seite hießen bis dahin „Was ist neu?".
 * Diesen Namen trägt jetzt der Knopf am Fuß des Menüs (lib/was-ist-neu.ts: die
 * kurzen Sätze für Martin) — zwei Wege mit demselben Namen und verschiedenem
 * Inhalt im selben Menü waren einer zu viel. `SEITEN_NAME` ist der EINE Name
 * der Seite; der Menüpunkt in index.html und Vesperas Prompt
 * (agent/persona.ts) nennen denselben (Test: tests/frontend/was-ist-neu.test.ts).
 */

import { buildPageHeader, requestMenuClose , buildLoadingHint } from "./sheet-ui.js";
import { renderMarkdown } from "./markdown.js";

/** Wie die Seite heißt — und der Menüpunkt, der zu ihr führt. */
export const SEITEN_NAME = "Alle Änderungen";

/**
 * Entfernt den Datei-Kopf, den die Seite schon selbst traegt.
 *
 * 2026-08-26 (Norm B9): Die ersten zwei Zeilen der Karte sagten nichts Neues —
 * "# Changelog" benennt zum zweiten Mal, was der Seitenkopf schon nennt
 * (`SEITEN_NAME`), und der Hinweis auf Keep-a-Changelog richtet sich an
 * Entwickler, nicht an den Leser dieser Seite.
 */
function ohneDoppeltenKopf(markdown: string): string {
  const zeilen = markdown.split(/\r?\n/);
  let ab = 0;
  if (zeilen[0]?.trim().toLowerCase() === "# changelog") {
    ab = 1;
    // Der erklaerende Absatz direkt darunter gehoert zum Kopf, nicht zum Inhalt.
    while (ab < zeilen.length && zeilen[ab].trim() === "") ab++;
    if (/keepachangelog|semver|semantic versioning/i.test(zeilen[ab] ?? "")) {
      while (ab < zeilen.length && zeilen[ab].trim() !== "") ab++;
    }
  }
  while (ab < zeilen.length && zeilen[ab].trim() === "") ab++;
  return zeilen.slice(ab).join("\n");
}

export async function openChangelogSheet(): Promise<void> {
  // 2026-07-05 (Martin): echtes Menue-Schliessen statt totem Alt-Hack.
  requestMenuClose();

  document.querySelector(".changelog-sheet")?.remove();

  const el = document.createElement("div");
  el.className = "sheet changelog-sheet page-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");

  const close = (): void => el.remove();

  const card = document.createElement("div");
  card.className = "sheet-card";
  // 2026-07-06 (Martin, roter Faden): Standard-Kopf wie alle Vollseiten.
  card.appendChild(buildPageHeader(SEITEN_NAME, close));

  const body = document.createElement("div");
  body.className = "changelog-body page-body";
  // 2026-08-30 (Paket 6): mit Zeichen statt nur Text.
  body.replaceChildren(buildLoadingHint("Lade Changelog …"));
  card.appendChild(body);
  el.appendChild(card);
  document.body.appendChild(el);

  // Escape schließt.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      close();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);

  try {
    const r = await fetch("/api/changelog", { credentials: "same-origin" });
    const j = (await r.json().catch(() => null)) as {
      ok?: boolean;
      markdown?: string;
    } | null;
    if (r.ok && j?.ok && j.markdown) {
      body.textContent = "";
      renderMarkdown(body, ohneDoppeltenKopf(j.markdown));
    } else {
      body.textContent = "Changelog konnte nicht geladen werden.";
    }
  } catch {
    body.textContent = "Changelog konnte nicht geladen werden.";
  }
}
